import React, { ComponentType, FC, useEffect } from "react";

import {
  DataViewKey,
  MediaType,
  QueryParamElement,
  useCurrentUser,
  useStatsViewHit,
} from "@keepeek/commons";
import { PageOverrideKey } from "@keepeek/refront-customers";
import { LinearProgress } from "@mui/material";
import { useRouter } from "next/dist/client/router";
import { useTranslation } from "react-i18next";
import { useRecoilValue } from "recoil";

import {
  mediaDetailModeMapping,
  SpecificMediaDetailProps,
} from "../components/detail/modes/MediaDetailMode";
import { CONFIGURATION_SECTION_PATH } from "../components/layout/adminMenu";
import MediaDetailLayout from "../components/layout/detail/MediaDetailLayout";
import MediaPageLayout from "../components/layout/detail/MediaPageLayout";
import { PagesPath } from "../containers/App/utils";
import { needPlayer } from "../containers/KViewer";
import { widgetStatesAtom, WidgetUIState } from "../containers/widgets/atoms";
import KFooterWidget from "../containers/widgets/KFooter";
import { LEGACY_FOOTER_ID } from "../containers/widgets/KFooter/utils";
import { KTab } from "../containers/widgets/KTabsWidget";
import KTemplates from "../containers/widgets/KTemplates/index";
import { getWidgetsByTemplates } from "../containers/widgets/utils/getWidgetsByTemplates";
import { injectElementIdInConfigurationIfNeeded } from "../containers/widgets/utils/injectElementIdInConfigurationIfNeeded";
import { isDisplayedTab } from "../containers/widgets/utils/isWidgetsRendered";
import { getCustomerOverrideComponent } from "../lib/overrides";
import { parseNumericParameter } from "../lib/query-util";
import { FooterWidget } from "../models/configuration/components/footerWidget";
import { TabsWidget } from "../models/configuration/components/tabsWidget";
import { Footer } from "../models/configuration/definitions/footer";
import { Global, Visibility } from "../models/configuration/global";
import { Type } from "../models/configuration/pages/home";
import { Theme } from "../models/configuration/theme";
import { backContextState } from "../providers/backContext/atoms";
import { useConfiguration, useKTemplatesConf } from "../providers/config/hooks";
import { useInitConfig } from "../providers/config/hooks/init";
import { useHeaderMetas } from "../providers/config/hooks/useHeaderMetas";
import { isPageVisible, KTemplatesTemplatesName } from "../providers/config/utils";
import { useElement } from "../providers/element/hooks";

const ElementPage: FC<React.PropsWithChildren<unknown>> = function ElementPage() {
  const router = useRouter();
  const backContext = useRecoilValue(backContextState);

  const configGlobal = useConfiguration<Global>(CONFIGURATION_SECTION_PATH.GLOBAL);

  useEffect(() => {
    if (!isPageVisible(configGlobal, Visibility.Element)) {
      router.push(PagesPath.ERROR_404);
    }
  }, [configGlobal, router]);

  const { elementId, mode, element } = useElement();
  const { currentUser: user } = useCurrentUser();
  const { t } = useTranslation();
  const { initConfig } = useInitConfig();
  const mediaTypeComputed = element?.formType || MediaType.OTHER;

  // STAT HIT VIEW
  useStatsViewHit({
    element,
    queryId: parseNumericParameter(router.query.id),
    userId: user?.id,
    needPlayer: needPlayer(mediaTypeComputed),
    ip: initConfig?.clientIp,
  });
  const pageWidgets = useRecoilValue(widgetStatesAtom);

  useHeaderMetas({
    title: `${t("page.element.title")}${element?.title?.value ? ` - ${element.title.value}` : ""}`,
    og: { image: element?.previewUrl },
  });

  const SpecificMediaDetail: ComponentType<React.PropsWithChildren<SpecificMediaDetailProps>> =
    mediaDetailModeMapping[mode];

  const themeConfiguration = useConfiguration<Theme>(CONFIGURATION_SECTION_PATH.THEME);

  const {
    loading: loadingDetailWidget,
    widgetsConfiguration: widgetsDetailConfiguration,
    templates: templatesDetailConfiguration,
  } = useKTemplatesConf({
    templatePagePosition: CONFIGURATION_SECTION_PATH.PAGES_ELEMENT,
    defaultTemplates: [
      {
        columns: [
          {
            widgets: [
              {
                id: "defaultWidgetSheet",
                type: Type.Sheet,
              },
            ],
          },
        ],
      },
    ],
  });

  const { loading, widgetsConfiguration, templates } = useKTemplatesConf({
    templatePagePosition: CONFIGURATION_SECTION_PATH.PAGES_ELEMENT,
    templatesName: KTemplatesTemplatesName.TemplatesLayout,
  });

  const {
    widgetsConfiguration: widgetsConfigurationFooter,
    templates: templatesFooter,
    loading: loadingFooter,
  } = useKTemplatesConf({
    templatePagePosition: CONFIGURATION_SECTION_PATH.PAGES_ELEMENT,
    templatesName: KTemplatesTemplatesName.TemplatesFooter,
  });

  // Management of backward compatibility
  const footerConfiguration = useConfiguration<Footer>(
    CONFIGURATION_SECTION_PATH.COMPONENTS_FOOTER,
  );
  let widgetsConfigurationFooterRetro = false;
  if ((!templatesFooter || templatesFooter.length === 0) && footerConfiguration) {
    widgetsConfigurationFooterRetro = true;
  }

  const highlightAssociations: boolean =
    router.query.mode === QueryParamElement.HIGHLIGHT_ASSOCIATIONS;
  const widgetAssociation =
    highlightAssociations && pageWidgets.find((w) => w.type === Type.Associations);

  const detailWidgets = getWidgetsByTemplates(templatesDetailConfiguration);
  const tabWidget = detailWidgets?.find((w) => w.type === Type.Tabs);

  let tabToClick: HTMLElement | null = null;

  if (tabWidget && widgetsDetailConfiguration) {
    const tabWidgetConfiguration: TabsWidget = widgetsDetailConfiguration[
      `tabs - ${tabWidget?.id}`
    ] as TabsWidget;

    const tabs: KTab[] = tabWidgetConfiguration?.tabs as KTab[];
    for (const index in tabs) {
      if (
        tabs[index] &&
        tabs[index].widgetsConfiguration !== null &&
        widgetAssociation &&
        isDisplayedTab(tabs[index], [widgetAssociation])
      ) {
        tabToClick = document.getElementById(`scrollable-auto-tab-${index}`);
      }
    }
  }
  // Highlight association widget
  useEffect(() => {
    if (
      highlightAssociations &&
      widgetAssociation &&
      widgetAssociation.state === WidgetUIState.RENDERED
    ) {
      // If there is a tab widget with associationWidget in one tab, click on this tab
      if (tabToClick) {
        tabToClick.click();
      }

      const widgetAssociationElements: HTMLCollectionOf<Element> = document.getElementsByClassName(
        `AssociationsWidget-${widgetAssociation.id}`,
      );

      // If an association widget is founded in the page, highlight it
      if (widgetAssociationElements[0]) {
        widgetAssociationElements[0].setAttribute(
          "style",
          `animation-name: highlightWidget; animation-duration: 2s; animation-iteration-count: 1;`,
        );
      }
    }
  }, [highlightAssociations, tabToClick, widgetAssociation]);

  return (
    <MediaPageLayout
      templateFooter={
        (loadingFooter && <LinearProgress />) ||
        (!loadingFooter && !widgetsConfigurationFooterRetro && (
          <KTemplates
            templatesConfiguration={templatesFooter || []}
            widgetsConfiguration={widgetsConfigurationFooter || null}
            themeConfiguration={themeConfiguration || null}
            style={{ height: "100%" }}
            dataViewKey={backContext.key || DataViewKey.SEARCH}
          />
        )) ||
        (!loadingFooter && widgetsConfigurationFooterRetro && footerConfiguration && (
          <KFooterWidget
            type={Type.Footer}
            widgetId={LEGACY_FOOTER_ID}
            footerConfiguration={footerConfiguration as FooterWidget}
          />
        ))
      }
    >
      {loading && <LinearProgress />}
      {!loading && (
        <KTemplates
          templatesConfiguration={templates || []}
          widgetsConfiguration={widgetsConfiguration || null}
          themeConfiguration={themeConfiguration || null}
          dataViewKey={backContext.key || DataViewKey.SEARCH}
        />
      )}
      <MediaDetailLayout>
        {loadingDetailWidget && <LinearProgress />}
        {!loadingDetailWidget && (
          <SpecificMediaDetail
            templatesConfiguration={templatesDetailConfiguration || []}
            widgetsConfiguration={
              injectElementIdInConfigurationIfNeeded({
                elementId,
                widgetsConfiguration: widgetsDetailConfiguration,
              }) || null
            }
          />
        )}
      </MediaDetailLayout>
    </MediaPageLayout>
  );
};
export default getCustomerOverrideComponent(PageOverrideKey.Element, ElementPage);
